// Import utility functions
import { FormatCurrency } from "global/utilities/ConvertCurrency";

// Import CSS
import "./RefundBreakdown.css";



// This displays the financial calculation behind an order refund
const RefundBreakdown = ({ refundBreakdown }) => {

    // This stores the refund values safely
    const refund = refundBreakdown?.refund || {};

    // This stores the calculation values safely
    const calculation = refundBreakdown?.calculation || {};

    // This stores the original order values safely
    const orderValues = refundBreakdown?.order_values || {};

    // This stores the payment provider information safely
    const paymentProvider = refundBreakdown?.psp || {};

    // This stores the resulting balance transaction safely
    const result = refundBreakdown?.result || {};

    // This stores the refund currency
    const currency = refund?.currency || result?.currency;

    // This stores the gross amount refunded to the customer
    const grossRefund = Number(refundBreakdown?.inc_tax ?? refund?.gross_major ?? 0);

    // This stores the recoverable tax amount
    const recoverableTax = Number(refundBreakdown?.tax ?? 0);

    // This stores the amount deducted from the user's balance
    const balanceDeduction = Math.abs(
        Number(refundBreakdown?.exc_tax ?? 0)
    );

    // This stores whether the refund relates to a cancelled order
    const isCancelledOrder = calculation?.is_cancelled_order === true;

    // This stores whether production had started before the refund
    const productionStarted = calculation?.production_started === true;

    // This stores whether the cancellation happened before production
    const isUnproducedCancellation =
        calculation?.is_unproduced_cancellation === true;

    // This stores whether the tax recovery had to be limited
    const recoverableTaxClamped =
        calculation?.recoverable_tax_clamped === true;



    // Do not render the breakdown if no currency is available
    if (!currency) {

        // Return no component
        return null;

    }



    // This renders the refund breakdown
    return (

        // This is the main refund breakdown wrapper
        <div className="refund-breakdown">

            {/* This is the refund breakdown header */}
            <div className="refund-breakdown__header">

                {/* This is the refund breakdown title */}
                <h3 className="refund-breakdown__title">
                    Refund breakdown
                </h3>

                {/* This explains how the balance deduction was calculated */}
                <p className="refund-breakdown__subtitle">
                    The customer received a refund of{" "}
                    {FormatCurrency(currency, grossRefund, false)}. Recoverable
                    tax was removed from that amount before the remaining refund
                    was deducted from your balance.
                </p>

            </div>

            {/* This contains the refund calculation steps */}
            <div className="refund-breakdown__steps">

                {/* This shows the total amount refunded to the customer */}
                <div className="refund-breakdown__step refund-breakdown__step--negative">

                    {/* This is the gross refund label */}
                    <span className="refund-breakdown__label">
                        Customer refund
                    </span>

                    {/* This is the gross refund amount */}
                    <strong className="refund-breakdown__value">
                        -
                        {FormatCurrency(
                            currency,
                            grossRefund,
                            false
                        )}
                    </strong>

                </div>

                {/* This shows the tax recovered from the refund */}
                <div className="refund-breakdown__step refund-breakdown__step--positive">

                    {/* This is the recoverable tax label */}
                    <span className="refund-breakdown__label">
                        Recoverable tax
                    </span>

                    {/* This is the recoverable tax amount */}
                    <strong className="refund-breakdown__value">
                        +
                        {FormatCurrency(
                            currency,
                            recoverableTax,
                            false
                        )}
                    </strong>

                </div>

            </div>

            {/* This provides further information about the refund */}
            <div className="refund-breakdown__items">

                {/* This shows the customer's total refund */}
                <div className="refund-breakdown__item">

                    {/* This is the detail label */}
                    <span className="refund-breakdown__item-title">
                        Refunded to customer
                    </span>

                    {/* This is the detail value */}
                    <strong className="refund-breakdown__value">
                        {FormatCurrency(
                            currency,
                            grossRefund,
                            false
                        )}
                    </strong>

                </div>

                {/* This shows the portion excluding tax */}
                <div className="refund-breakdown__item">

                    {/* This is the detail label */}
                    <span className="refund-breakdown__item-title">
                        Refund excluding tax
                    </span>

                    {/* This is the detail value */}
                    <strong className="refund-breakdown__value">
                        {FormatCurrency(
                            currency,
                            balanceDeduction,
                            false
                        )}
                    </strong>

                </div>

                {/* This shows the recoverable tax */}
                <div className="refund-breakdown__item">

                    {/* This is the detail label */}
                    <span className="refund-breakdown__item-title">
                        Tax recovered
                    </span>

                    {/* This is the detail value */}
                    <strong className="refund-breakdown__value">
                        {FormatCurrency(
                            currency,
                            recoverableTax,
                            false
                        )}
                    </strong>

                </div>

                {/* This optionally shows the payment provider */}
                {paymentProvider?.psp &&

                    // This shows the payment provider detail
                    <div className="refund-breakdown__item">

                        {/* This is the payment provider label */}
                        <span className="refund-breakdown__item-title">
                            Refunded through
                        </span>

                        {/* This is the payment provider name */}
                        <strong className="refund-breakdown__value">
                            {paymentProvider.psp}
                        </strong>

                    </div>

                }

                {/* This optionally shows the refund reference */}
                {refund?.reference &&

                    // This shows the refund reference detail
                    <div className="refund-breakdown__item">

                        {/* This is the reference label */}
                        <span className="refund-breakdown__item-title">
                            Refund reference
                        </span>

                        {/* This is the refund reference */}
                        <span className="refund-breakdown__reference">
                            {refund.reference}
                        </span>

                    </div>

                }

            </div>

            {/* This shows useful cancellation information when available */}
            {(isCancelledOrder || isUnproducedCancellation) &&

                // This contains the cancellation status message
                <div className="refund-breakdown__notice">

                    {/* This is the cancellation status title */}
                    <strong className="refund-breakdown__notice-title">
                        Cancellation refund
                    </strong>

                    {/* This explains the production state */}
                    <span className="refund-breakdown__notice-text">
                        {productionStarted
                            ? "Production had already started when this refund was processed."
                            : "Production had not started when this refund was processed."}
                    </span>

                </div>

            }

            {/* This warns when the recoverable tax was limited */}
            {recoverableTaxClamped &&

                // This contains the limited tax message
                <div className="refund-breakdown__notice">

                    {/* This is the notice title */}
                    <strong className="refund-breakdown__notice-title">
                        Tax recovery adjusted
                    </strong>

                    {/* This explains the adjustment */}
                    <span className="refund-breakdown__notice-text">
                        The recoverable tax was limited so that it did not exceed
                        the tax originally recorded against the order.
                    </span>

                </div>

            }

            {/* This shows the final balance deduction */}
            <div className="refund-breakdown__total">

                {/* This is the total label */}
                <span className="refund-breakdown__total-label">
                    Deducted from your balance
                </span>

                {/* This is the final balance deduction */}
                <strong className="refund-breakdown__total-value">
                    -
                    {FormatCurrency(
                        currency,
                        balanceDeduction,
                        false
                    )}
                </strong>

            </div>

            {/* This optionally shows the original order tax context */}
            {Number(orderValues?.inc_tax_basis_major || 0) > 0 &&

                // This contains the original order context
                <p className="refund-breakdown__footnote">

                    {/* This displays the original order amounts */}
                    The original order tax basis was{" "}
                    {FormatCurrency(
                        currency,
                        Number(orderValues.inc_tax_basis_major),
                        false
                    )}
                    , including{" "}
                    {FormatCurrency(
                        currency,
                        Number(orderValues.recoverable_tax_major || 0),
                        false
                    )}{" "}
                    of recoverable tax.

                </p>

            }

        </div>

    );

};

// Export the refund breakdown component
export default RefundBreakdown;